import { Check, Close, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  Typography,
  useEventCallback,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { Fragment, memo, useEffect, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import {
  CitizenshipFormVM,
  RegistrationCitizenshipFormVM,
} from 'mutation/view-models/registration-citizenship';
import ReasonCitizenship from './reason-citizenship-list';
import FormReasonCitizenship from './form';
import { initialValueCitizenship } from 'mutation/utils/transformer-registration-citizenship';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { MotArtCitizenship } from 'shell/models/mutation/mot-art-citizenship';
import { transformerCitizenshipFromModel } from 'mutation/utils/transformer-citizenship-from-model';
import { ArticleVM } from './form/shared/article-list';

interface RegistrationCitizenshipProps {
  isNationalSearch?: string;
  disabled?: boolean;
}

const RegistrationCitizenshipComponent = ({
  isNationalSearch,
  disabled,
}: RegistrationCitizenshipProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const formContext = useFormikContext<RegistrationCitizenshipFormVM>();

  const onChangeReasonCitizenship = useEventCallback((event: SelectChangeEvent<string>) => {
    const newValues = {
      ...formContext.values,
      newCitizenship: {
        // clear all
        ...initialValueCitizenship,
        reasonCitizenship: event?.target?.value || '',
      },
    };

    formContext.setValues(newValues);
  });

  const [dataDecodifica, getDataDecodifica] = useHttpFetch<
    HttpResponse<MotArtCitizenship>,
    HttpResponse<MotArtCitizenship>
  >(`${__PERSON_NEW_API_URL__}/leggi-sulla-cittadinanza/decodifica`, {
    onBootstrap: false,
    // initialData: {},
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (res) => res.data,
  });

  useEffect(() => {
    if (formContext.values.newCitizenship?.idMotivoVariazioneCittadinanza) {
      getDataDecodifica({
        data: {
          idMotivoVariazioneCittadinanza:
            formContext.values.newCitizenship?.idMotivoVariazioneCittadinanza,
        },
      });
    }
  }, [formContext.values.newCitizenship]);

  useEffect(() => {
    const castedDecodifica = dataDecodifica?.data as unknown as HttpResponse<MotArtCitizenship>;

    const subEvento = castedDecodifica?.payload?.key?.subEvento;

    const idMotivoVariazioneCittadinanza = `${castedDecodifica?.payload?.key?.anno || ''}.${
      castedDecodifica?.payload?.key?.articolo || ''
    }.${castedDecodifica?.payload?.key?.comma || ''}.${
      castedDecodifica?.payload?.key?.numero || ''
    }.${castedDecodifica?.payload?.key?.parte || ''}.${
      castedDecodifica?.payload?.key?.punto || ''
    }.${castedDecodifica?.payload?.key?.subEvento || ''}`;

    const motivoVariazioneCittadinanza: ArticleVM | undefined = idMotivoVariazioneCittadinanza
      ? {
          id: idMotivoVariazioneCittadinanza,
          value: castedDecodifica?.payload?.descrizioneArticolo || '',
          subEvent: castedDecodifica?.payload?.key?.subEvento || '',
          el: castedDecodifica?.payload?.descrizioneBreve
            ? {
                descrizioneBreve: castedDecodifica.payload.descrizioneBreve,
              }
            : undefined,
        }
      : undefined;

    if (subEvento) {
      const newValues = {
        ...formContext.values,
        ...transformerCitizenshipFromModel(
          formContext.values.modelEventoDTO,
          formContext.values.datiAggiuntiviSide,
          subEvento?.toString(),
          motivoVariazioneCittadinanza
        ),
        // newCitizenship: {
        //   // clear all
        //   ...initialValueCitizenship,
        //   reasonCitizenship: subEvento?.toString(),
        // },
      };

      formContext.setValues(newValues);
    }
  }, [dataDecodifica]);

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-person-content"
          id="panel-person-header"
          expandIcon={<ExpandMore />}
        >
          {!!formContext.errors.newCitizenship ? (
            <Close color="error" />
          ) : (
            <Check color="success" />
          )}

          <Typography>Registrazione Cittadinanza</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid item xs={12} md={8}>
            <ReasonCitizenship
              required
              onChange={onChangeReasonCitizenship}
              value={formContext.values.newCitizenship?.reasonCitizenship || ''}
              error={
                Boolean(
                  (formContext.errors.newCitizenship as FormikErrors<CitizenshipFormVM>)
                    ?.reasonCitizenship
                ) &&
                (formContext.touched.newCitizenship as unknown as FormikTouched<CitizenshipFormVM>)
                  ?.reasonCitizenship
              }
              disabled={disabled}
            />
          </Grid>
          <FormReasonCitizenship isNationalSearch={isNationalSearch} disabled={disabled} />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

RegistrationCitizenshipComponent.displayName = 'Accordion Registration Citizenship component';

const RegistrationCitizenship = memo(RegistrationCitizenshipComponent);

export default RegistrationCitizenship;
